import { useMemo } from 'react';

import { Typography, useTheme } from '@mui/material';

import clsx from 'clsx';
import { format, fromUnixTime, getMonth, getUnixTime, getYear } from 'date-fns';
import _first from 'lodash/first';
import _last from 'lodash/last';
import _max from 'lodash/max';
import _min from 'lodash/min';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';

import { formatUserDateFromString } from '@arrived/common';

import { Money } from '../Money';

import { useIsMobile } from '../hooks/useIsMobile';

export interface Point {
  x: number;
  y: number;
}

interface XAxisTickProps extends Partial<Point> {
  payload?: {
    value: Date | number | string;
  };
}

const XAxisTick = ({ x, y, payload }: XAxisTickProps) => {
  const parsedPayload = useMemo(() => {
    try {
      if (payload?.value) {
        if (payload.value instanceof Date) {
          return format(payload.value, 'yyyy');
        }

        if (typeof payload.value === 'number') {
          return format(fromUnixTime(payload.value), 'yyyy');
        }

        if (typeof payload.value === 'string') {
          return formatUserDateFromString(payload.value, 'yyyy');
        }
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  }, [payload]);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fontSize="smaller"
        fill="#666"
        transform="rotate(-35)"
        className="tick-text x-tick-text"
      >
        {parsedPayload}
      </text>
    </g>
  );
};

interface YAxisTickProps extends Partial<Point> {
  payload?: {
    value?: number;
  };
}

const YAxisTick = ({ x, y, payload }: YAxisTickProps) => (
  <g transform={`translate(${x},${y})`}>
    <text x={0} y={0} dy={16} textAnchor="end" fontSize="smaller" fill="#666" className="tick-text y-tick-text">
      <Money value={payload?.value} isCompact={true} />
    </text>
  </g>
);

interface TimesSeriesGraphViewProps {
  className?: string;
  height?: number;
  rawData: Point[];
}

const StyledTimeSeriesGraphView = styled(ResponsiveContainer)`
  &.timeseries-graph-container {
    .timeseries-graph-tooltip {
      width: 100%;
      height: 100%;
      box-shadow: 0 0 1px 1px #e7e7e7;

      .kpi {
        display: flex;
        width: 100%;
        height: 100%;
        flex-flow: column nowrap;
        align-items: center;
        justify-content: center;
      }
    }
  }
`;

export const TimeSeriesGraphView = ({ rawData, className, height }: TimesSeriesGraphViewProps) => {
  const isMobile = useIsMobile();
  const { palette } = useTheme();

  if (rawData.length === 0) {
    return null;
  }

  const values: number[] = rawData.map((p: Point) => p.y);
  const minValue: number = _min(values)!;
  const maxValue: number = _max(values)!;
  const deltaValue = maxValue - minValue;
  const deltaValuePerStep = deltaValue / 4;

  const yTicks: number[] = [];

  for (let i = 0; i < 5; i++) {
    yTicks.push(minValue + i * deltaValuePerStep);
  }

  const xTicks: number[] = [];
  const minYear = getYear(fromUnixTime(rawData[0].x));
  const maxYear = getYear(fromUnixTime(_last(rawData)!.x));
  const lastMonth = getMonth(fromUnixTime(_last(rawData)!.x));

  for (let year = minYear + 1; year <= maxYear; isMobile ? (year += 2) : year++) {
    xTicks.push(getUnixTime(new Date(year, lastMonth, 1, 0, 0, 0)));
  }

  const data = rawData.map((p: Point) => ({ date: p.x, value: p.y }));

  return (
    <StyledTimeSeriesGraphView
      className={clsx('timeseries-graph-container', className)}
      width="100%"
      height={height ?? 200}
    >
      <LineChart data={data} margin={{ top: 24, right: 18, left: 0, bottom: 18 }}>
        <XAxis
          type="number"
          dataKey="date"
          tick={<XAxisTick />}
          ticks={xTicks}
          interval={0}
          domain={[_first(xTicks)!, _last(xTicks)!]}
        />
        <YAxis
          type="number"
          dataKey="value"
          tick={<YAxisTick />}
          ticks={yTicks}
          interval={0}
          domain={[minValue, maxValue]}
        />
        <CartesianGrid stroke={palette.neutrals.platinum} />
        <Tooltip
          wrapperStyle={{
            backgroundColor: `rgba(255, 255, 255, 0.7)`,
            width: `130px`,
            height: `80px`,
            boxShadow: `box-shadow: 0 0 1px 1px #e7e7e7`,
          }}
          content={(props: Readonly<TooltipProps<number, number>>) => {
            const { payload } = props || {};
            if (!payload || (payload && payload.length === 0)) {
              return null;
            }

            return (
              <div className="timeseries-graph-tooltip">
                <div className="kpi">
                  <Typography variant="body1">
                    <Money value={payload[0].value || 0} />
                  </Typography>
                  <Typography variant="body1">
                    {format(fromUnixTime(props.payload![0].payload.date), 'MMM, yyyy')}
                  </Typography>
                </div>
              </div>
            );
          }}
        />
        <Line dataKey="value" stroke={palette.primary.main} type="monotone" dot={false} />
      </LineChart>
    </StyledTimeSeriesGraphView>
  );
};
